import { usePatientImagingOrders, usePatientLabOrder, usePatientVitalSigns } from '@hook'
import { DoctorModal } from '@modal'
import { Tab2 } from '@ui'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import ImagingResultsTable from './ImagingResultsTable'
import LaboratoryResultsTable from './LaboratoryResultsTable'
import ResultsTableLayout from './tableLayout'
import VitalsResultsTable from './VitalsResultsTable'

type ResultsTab = {
  isLoading?: boolean
  label: string
  records: any[]
  renderTable: (records: any[]) => JSX.Element
  searchKey: string
  value: string
}

const VALID_TABS = ['vitals', 'laboratory', 'imaging'] as const

export default function Results() {
  const [searchParams, setSearchParams] = useSearchParams()
  const tabFromUrl = searchParams.get('tab') || ''
  const initialTab = VALID_TABS.includes(tabFromUrl as (typeof VALID_TABS)[number]) ? tabFromUrl : 'laboratory'
  const [activeTab, setActiveTab] = useState(initialTab)

  const { included: labIncluded, isLoading: labIsLoading, labOrders } = usePatientLabOrder({})
  const { imagingOrders, included: imagingIncluded, isLoading: imagingIsLoading } = usePatientImagingOrders({})
  const { vitals, isLoading: vitalsIsLoading } = usePatientVitalSigns({})

  useEffect(() => {
    const next = searchParams.get('tab')
    if (next && VALID_TABS.includes(next as (typeof VALID_TABS)[number]) && next !== activeTab) {
      setActiveTab(next)
    }
  }, [searchParams, activeTab])

  const handleTabChange = (value: string) => {
    setActiveTab(value)
    setSearchParams(
      prev => {
        prev.set('tab', value)
        return prev
      },
      { replace: true }
    )
  }

  const resultsTabs: ResultsTab[] = [
    {
      isLoading: vitalsIsLoading,
      label: 'Vitals',
      records: vitals || [],
      renderTable: records => <VitalsResultsTable isLoading={vitalsIsLoading} records={records as any} />,
      searchKey: 'vitalsResultsSearch',
      value: 'vitals'
    },
    {
      isLoading: labIsLoading,
      label: 'Laboratory',
      records: labOrders?.data || [],
      renderTable: records => (
        <LaboratoryResultsTable included={labIncluded || []} isLoading={labIsLoading} records={records as any} />
      ),
      searchKey: 'labResultsSearch',
      value: 'laboratory'
    },
    {
      isLoading: imagingIsLoading,
      label: 'Imaging',
      records: imagingOrders?.data || [],
      renderTable: records => (
        <ImagingResultsTable included={imagingIncluded || []} isLoading={imagingIsLoading} records={records as any} />
      ),
      searchKey: 'imagingResultsSearch',
      value: 'imaging'
    }
  ]

  const currentTab = resultsTabs.find(tab => tab.value === activeTab) || resultsTabs[0]

  return (
    <div className="flex min-h-0 flex-col gap-5">
      <div className="w-max max-w-full">
        <Tab2 activeTab={activeTab} background="#FFFFFF" setActiveTab={handleTabChange} tabs={resultsTabs} />
      </div>

      <ResultsTableLayout
        key={currentTab.value}
        records={currentTab.records}
        searchKey={currentTab.searchKey}
        title={currentTab.label}
      >
        {records => currentTab.renderTable(records)}
      </ResultsTableLayout>
      <DoctorModal />
    </div>
  )
}
